import { HttpClient, HttpErrorResponse, HttpEvent, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MonoTypeOperatorFunction, retry, timer } from "rxjs";
import { Observable } from "rxjs/internal/Observable";
import type {
  IHttpClientBaseOptions,
  IHttpClientBodyArrayBufferOptions,
  IHttpClientBodyBlobOptions,
  IHttpClientBodyJsonOptions,
  IHttpClientBodyTextOptions,
  IHttpClientEventArraybufferOptions,
  IHttpClientEventBlobOptions,
  IHttpClientEventJsonOptions,
  IHttpClientEventTextOptions,
  IHttpClientResponseArraybufferOptions,
  IHttpClientResponseBlobOptions,
  IHttpClientResponseJsonOptions,
  IHttpClientResponseTextOptions,
  IHttpRequestClientBaseOptions,
  IHttpRequestClientBodyArrayBufferOptions,
  IHttpRequestClientBodyBlobOptions,
  IHttpRequestClientBodyJsonOptions,
  IHttpRequestClientBodyTextOptions,
  IHttpRequestClientEventArraybufferOptions,
  IHttpRequestClientEventBlobOptions,
  IHttpRequestClientEventJsonOptions,
  IHttpRequestClientEventTextOptions,
  IHttpRequestClientResponseArraybufferOptions,
  IHttpRequestClientResponseBlobOptions,
  IHttpRequestClientResponseJsonOptions,
  IHttpRequestClientResponseTextOptions,
} from "../../models/http-client/_http.export";

@Injectable({ providedIn: "root" })
export class RetryHttpClient {
  private readonly maxRetries = 3;
  private readonly delayMs = 1000;

  constructor(private readonly http: HttpClient) {}

  request<D, R>(method: string, url: string, options?: IHttpRequestClientBodyJsonOptions<D>): Observable<R>;
  request<D>(method: string, url: string, options: IHttpRequestClientBodyArrayBufferOptions<D>): Observable<ArrayBuffer>;
  request<D>(method: string, url: string, options: IHttpRequestClientBodyTextOptions<D>): Observable<string>;
  request<D>(method: string, url: string, options: IHttpRequestClientBodyBlobOptions<D>): Observable<Blob>;
  request<D>(method: string, url: string, options: IHttpRequestClientEventArraybufferOptions<D>): Observable<HttpEvent<ArrayBuffer>>;
  request<D>(method: string, url: string, options: IHttpRequestClientEventBlobOptions<D>): Observable<HttpEvent<Blob>>;
  request<D>(method: string, url: string, options: IHttpRequestClientEventTextOptions<D>): Observable<HttpEvent<string>>;
  request<D, R>(method: string, url: string, options: IHttpRequestClientEventJsonOptions<D>): Observable<HttpEvent<R>>;
  request<D>(method: string, url: string, options: IHttpRequestClientResponseArraybufferOptions<D>): Observable<HttpResponse<ArrayBuffer>>;
  request<D>(method: string, url: string, options: IHttpRequestClientResponseBlobOptions<D>): Observable<HttpResponse<Blob>>;
  request<D>(method: string, url: string, options: IHttpRequestClientResponseTextOptions<D>): Observable<HttpResponse<string>>;
  request<D, R>(method: string, url: string, options: IHttpRequestClientResponseJsonOptions<D>): Observable<HttpResponse<R>>;
  request<D, R>(method: string, url: string, options?: IHttpRequestClientBaseOptions<D>): Observable<R> {
    return this.http.request<R>(method, url, options).pipe(this.retryOn5xx<R>());
  }

  get<R>(url: string, options?: IHttpClientBodyJsonOptions): Observable<R>;
  get(url: string, options: IHttpClientBodyArrayBufferOptions): Observable<ArrayBuffer>;
  get(url: string, options: IHttpClientBodyTextOptions): Observable<string>;
  get(url: string, options: IHttpClientBodyBlobOptions): Observable<Blob>;
  get(url: string, options: IHttpClientEventArraybufferOptions): Observable<HttpEvent<ArrayBuffer>>;
  get(url: string, options: IHttpClientEventBlobOptions): Observable<HttpEvent<Blob>>;
  get(url: string, options: IHttpClientEventTextOptions): Observable<HttpEvent<string>>;
  get<R>(url: string, options: IHttpClientEventJsonOptions): Observable<HttpEvent<R>>;
  get(url: string, options: IHttpClientResponseArraybufferOptions): Observable<HttpResponse<ArrayBuffer>>;
  get(url: string, options: IHttpClientResponseBlobOptions): Observable<HttpResponse<Blob>>;
  get(url: string, options: IHttpClientResponseTextOptions): Observable<HttpResponse<string>>;
  get<R>(url: string, options: IHttpClientResponseJsonOptions): Observable<HttpResponse<R>>;
  get<R>(url: string, options?: IHttpClientBaseOptions): Observable<R> {
    return this.http.get<R>(url, options).pipe(this.retryOn5xx<R>());
  }

  post<D, R>(url: string, body: D, options?: IHttpClientBodyJsonOptions): Observable<R>;
  post<D>(url: string, body: D, options: IHttpClientBodyArrayBufferOptions): Observable<ArrayBuffer>;
  post<D>(url: string, body: D, options: IHttpClientBodyTextOptions): Observable<string>;
  post<D>(url: string, body: D, options: IHttpClientBodyBlobOptions): Observable<Blob>;
  post<D>(url: string, body: D, options: IHttpClientEventArraybufferOptions): Observable<HttpEvent<ArrayBuffer>>;
  post<D>(url: string, body: D, options: IHttpClientEventBlobOptions): Observable<HttpEvent<Blob>>;
  post<D>(url: string, body: D, options: IHttpClientEventTextOptions): Observable<HttpEvent<string>>;
  post<D, R>(url: string, body: D, options: IHttpClientEventJsonOptions): Observable<HttpEvent<R>>;
  post<D>(url: string, body: D, options: IHttpClientResponseArraybufferOptions): Observable<HttpResponse<ArrayBuffer>>;
  post<D>(url: string, body: D, options: IHttpClientResponseBlobOptions): Observable<HttpResponse<Blob>>;
  post<D>(url: string, body: D, options: IHttpClientResponseTextOptions): Observable<HttpResponse<string>>;
  post<D, R>(url: string, body: D, options: IHttpClientResponseJsonOptions): Observable<HttpResponse<R>>;
  post<D, R>(url: string, body: D, options?: IHttpClientBaseOptions): Observable<R> {
    return this.http.post<R>(url, body, options).pipe(this.retryOn5xx<R>());
  }

  postResponseWithoutRetry<B, R>(url: string, body: B, options: IHttpClientResponseJsonOptions & { observe: "response" }): Observable<HttpResponse<R>> {
    return this.http.post<R>(url, body, { ...options, observe: "response" as const });
  }

  put = this.clonePostAs("put");
  patch = this.clonePostAs("patch");

  private clonePostAs(method: "put" | "patch") {
    return <D, R>(url: string, body: D, options?: IHttpClientBaseOptions): Observable<R> => this.http[method]<R>(url, body, options).pipe(this.retryOn5xx<R>());
  }

  private shouldRetry(err: HttpErrorResponse, retryCount: number): boolean {
    if (err.status === 555) {
      return false;
    }

    if (err.status === 0) {
      return retryCount < this.maxRetries;
    }

    return err.status >= 500 && err.status < 600 && retryCount < this.maxRetries;
  }

  private retryOn5xx<T>(): MonoTypeOperatorFunction<T> {
    return retry({
      count: this.maxRetries,
      delay: (error: HttpErrorResponse, retryCount: number) => {
        if (this.shouldRetry(error, retryCount)) {
          return timer(this.delayMs);
        }
        throw error;
      },
    });
  }
}
